import { useEffect, useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    SimpleGrid,
    Input,
    Textarea,
    Icon,
} from "@chakra-ui/react";
import { FaEnvelope, FaMapMarkerAlt, FaPaperPlane } from "react-icons/fa";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import { fetchSiteSettings } from "../../../services/strapi/queries";
import type { SiteSettings } from "../../../services/strapi/types";
import { baseURL } from "../../../services/constants";
import {
    DoodleUnderline,
    DoodleSparkle,
    WashiTape,
    DoodlePaperclip,
} from "../../CustomUi/DoodleIcons";

const FALLBACK_SETTINGS: SiteSettings = {
    contactEmail: "hello@result.ng",
    emailResponseTime: "We respond within one business day",
    location: "Lagos, Nigeria",
    locationBlurb: "Result HQ",
};

import { usePageTitle } from "../../../hooks/usePageTitle";

const ContactPage = () => {
    usePageTitle(
        "Contact Support & Sales",
        "Get in touch with the Result team in Lagos, Nigeria. We respond promptly to school inquiries, onboarding, and support."
    );
    const [settings, setSettings] = useState<SiteSettings>(FALLBACK_SETTINGS);
    const [form, setForm] = useState({
        name: "",
        schoolName: "",
        email: "",
        message: "",
        website: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchSiteSettings().then((doc) => {
            if (cancelled || !doc) return;
            setSettings({ ...FALLBACK_SETTINGS, ...doc });
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;
        setError(null);

        if (!form.name || !form.email || !form.message) {
            setError("Please fill in your name, email, and message.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${baseURL}/Public/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                throw new Error(body?.message ?? "Could not send your message. Try again in a moment.");
            }
            setSubmitted(true);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setSubmitting(false);
        }
    };

    const items = [
        {
            icon: FaEnvelope,
            label: "Email us",
            value: settings.contactEmail ?? FALLBACK_SETTINGS.contactEmail!,
            sub: settings.emailResponseTime ?? FALLBACK_SETTINGS.emailResponseTime!,
            href: `mailto:${settings.contactEmail ?? FALLBACK_SETTINGS.contactEmail}`,
            bg: "#FEF9C3",
        },
        {
            icon: FaMapMarkerAlt,
            label: "Location",
            value: settings.location ?? FALLBACK_SETTINGS.location!,
            sub: settings.locationBlurb ?? FALLBACK_SETTINGS.locationBlurb!,
            href: undefined,
            bg: "#E0F2FE",
        },
    ];

    return (
        <Box minH="100vh" bg="#FAF8F2">
            <PublicNavbar />

            {/* Hero */}
            <Box py={[16, 24]} bg="brand.900" color="white" position="relative" overflow="hidden">
                <Container maxW="4xl">
                    <VStack gap={5} textAlign="center">
                        <HStack gap={2} justify="center">
                            <DoodleSparkle size={20} color="#FBBF24" />
                            <Box
                                px={3.5}
                                py={1}
                                bg="whiteAlpha.150"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="whiteAlpha.200"
                            >
                                <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="gold.300">
                                    We&apos;re Listening
                                </Text>
                            </Box>
                            <DoodleSparkle size={20} color="#FBBF24" />
                        </HStack>

                        <Heading as="h1" fontSize={["3xl", "4xl", "5xl"]} fontWeight="extrabold">
                            Let&apos;s{" "}
                            <Box as="span" position="relative" display="inline-block" color="gold.400">
                                talk
                                <DoodleUnderline position="absolute" bottom="-8px" left="0" color="#F59E0B" height="14px" />
                            </Box>
                        </Heading>
                        <Text fontSize={["md", "lg"]} opacity={0.8} maxW="xl">
                            Have questions about Result, onboarding your teachers, or custom billing? We&apos;re here to help.
                        </Text>
                    </VStack>
                </Container>
            </Box>

            {/* Main content grid */}
            <Box py={[16, 24]} className="notebook-dots">
                <Container maxW="7xl">
                    <SimpleGrid columns={[1, 2]} gap={14} alignItems="start">
                        {/* Contact details */}
                        <VStack align="start" gap={8}>
                            <VStack align="start" gap={3}>
                                <Text fontSize="xs" fontWeight="extrabold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                    Direct Channels
                                </Text>
                                <Heading as="h2" fontSize={["2xl", "3xl"]} fontWeight="extrabold" color="gray.900" lineHeight="1.25">
                                    We&apos;re based in Lagos and respond quickly
                                </Heading>
                                <Text fontSize="sm" color="gray.600" lineHeight="1.8">
                                    Whether you&apos;re a school administrator, principal, IT lead, or consultant — reach out and our local team will attend to you promptly.
                                </Text>
                            </VStack>

                            <VStack align="start" gap={4} w="full">
                                {items.map((item) => {
                                    const content = (
                                        <HStack
                                            gap={4}
                                            w="full"
                                            align="center"
                                            p={4}
                                            bg={item.bg}
                                            borderRadius="xl"
                                            border="2px solid #0A3D2E"
                                            boxShadow="3px 3px 0px #0A3D2E"
                                            transition="all 0.15s ease"
                                            _hover={{ transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #0A3D2E" }}
                                        >
                                            <Box p={3} bg="white" borderRadius="xl" border="1.5px solid #0A3D2E" color="brand.900" flexShrink={0}>
                                                <Icon as={item.icon} fontSize="lg" />
                                            </Box>
                                            <VStack align="start" gap={0.5}>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                                    {item.label}
                                                </Text>
                                                <Text fontWeight="extrabold" color="gray.900" fontSize="sm">{item.value}</Text>
                                                <Text fontSize="xs" color="gray.600">{item.sub}</Text>
                                            </VStack>
                                        </HStack>
                                    );
                                    return item.href ? (
                                        <a key={item.label} href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" style={{ width: "100%" }}>
                                            {content}
                                        </a>
                                    ) : (
                                        <Box key={item.label} w="full">
                                            {content}
                                        </Box>
                                    );
                                })}
                            </VStack>
                        </VStack>

                        {/* Contact form as a notebook sheet */}
                        <Box
                            position="relative"
                            p={[6, 9]}
                            bg="white"
                            borderRadius="2xl"
                            border="3px solid #0A3D2E"
                            boxShadow="6px 8px 0px #0A3D2E"
                        >
                            <WashiTape angle={-3} top="-14px" left="32px" tapeColor="#FEF08A" label="Leave a note ✉️" />
                            <DoodlePaperclip color="#10B981" size={36} position="absolute" top="-16px" right="36px" />

                            {submitted ? (
                                <VStack gap={4} py={10} textAlign="center">
                                    <Box
                                        w={16}
                                        h={16}
                                        borderRadius="full"
                                        bg="#DCFCE7"
                                        border="2px solid #0A3D2E"
                                        color="brand.900"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontSize="2xl"
                                        fontWeight="extrabold"
                                        boxShadow="3px 3px 0px #0A3D2E"
                                    >
                                        ✓
                                    </Box>
                                    <Heading as="h3" fontSize="2xl" fontWeight="extrabold" color="gray.900">Message received!</Heading>
                                    <Text color="gray.600" maxW="sm" fontSize="sm">
                                        Thanks {form.name.split(" ")[0]}! We&apos;ll be in touch shortly — usually within one business day.
                                    </Text>
                                </VStack>
                            ) : (
                                <form onSubmit={handleSubmit} style={{ paddingTop: "8px" }}>
                                    <VStack gap={5}>
                                        {error && (
                                            <Box w="full" p={3} bg="#FEE2E2" border="2px solid #DC2626" color="#991B1B" borderRadius="xl" fontSize="xs" fontWeight="semibold">
                                                {error}
                                            </Box>
                                        )}

                                        <VStack align="start" w="full" gap={1.5}>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.800">Your Name *</Text>
                                            <Input
                                                bg="#FAF9F5"
                                                placeholder="e.g. Mrs. Funke Adeleke"
                                                size="md"
                                                borderRadius="xl"
                                                border="2px solid #0A3D2E"
                                                _focus={{ borderColor: "brand.700", bg: "white" }}
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                disabled={submitting}
                                                required
                                            />
                                        </VStack>

                                        <VStack align="start" w="full" gap={1.5}>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.800">School Name</Text>
                                            <Input
                                                bg="#FAF9F5"
                                                placeholder="e.g. Greenfield Academy"
                                                size="md"
                                                borderRadius="xl"
                                                border="2px solid #0A3D2E"
                                                _focus={{ borderColor: "brand.700", bg: "white" }}
                                                value={form.schoolName}
                                                onChange={(e) => setForm({ ...form, schoolName: e.target.value })}
                                                disabled={submitting}
                                            />
                                        </VStack>

                                        <VStack align="start" w="full" gap={1.5}>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.800">Email Address *</Text>
                                            <Input
                                                bg="#FAF9F5"
                                                type="email"
                                                placeholder="your@email.com"
                                                size="md"
                                                borderRadius="xl"
                                                border="2px solid #0A3D2E"
                                                _focus={{ borderColor: "brand.700", bg: "white" }}
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                disabled={submitting}
                                                required
                                            />
                                        </VStack>

                                        <VStack align="start" w="full" gap={1.5}>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.800">Message *</Text>
                                            <Textarea
                                                bg="#FAF9F5"
                                                placeholder="How can we help your school?"
                                                size="md"
                                                rows={4}
                                                borderRadius="xl"
                                                border="2px solid #0A3D2E"
                                                _focus={{ borderColor: "brand.700", bg: "white" }}
                                                value={form.message}
                                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                                disabled={submitting}
                                                required
                                            />
                                        </VStack>

                                        {/* Honeypot */}
                                        <Box position="absolute" left="-9999px" aria-hidden="true">
                                            <input
                                                type="text"
                                                name="website"
                                                tabIndex={-1}
                                                autoComplete="off"
                                                value={form.website}
                                                onChange={(e) => setForm({ ...form, website: e.target.value })}
                                            />
                                        </Box>

                                        <Button
                                            type="submit"
                                            w="full"
                                            size="lg"
                                            bg="brand.800"
                                            color="white"
                                            border="2px solid #06231A"
                                            boxShadow="3px 3px 0px #06231A"
                                            fontWeight="bold"
                                            borderRadius="xl"
                                            transition="all 0.15s ease"
                                            _hover={{ bg: "brand.900", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #06231A" }}
                                            disabled={submitting}
                                        >
                                            {submitting ? "Sending..." : "Send Message"} <Icon as={FaPaperPlane} ml={2} fontSize="sm" />
                                        </Button>
                                    </VStack>
                                </form>
                            )}
                        </Box>
                    </SimpleGrid>
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default ContactPage;
